import {
    IChangeExpiredPasswordOptions,
    IChangeExpiredPasswordResult, ICreateBookingSessionOptions,
    ICreateFlightSearchSessionOptions,
    IUserService
} from "./user.service.interface";
import {ServiceBase} from "../service-base";
import {IServiceFactory} from "../service-factory.interface";
import {IDotRezUserSession} from "../dot-rez-api/session/user-session/dot-rez-user.session.interface";
import {TimeSpan} from "../../types/time-span";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import {
    ILoginCredentials,
    ILoginWithFormOptions,
    IUserModel
} from "./models/user-model.interface";
import {AnonymousUserModel} from "./models/anonymous-user/anonymous-user.model";
import {AuthorizedUserModel} from "./models/authorized-user/authorized-user.model";
import {IDotRezBookingSession} from "../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {LoginAndRegistrationDialogTabEnum} from "./models/login-and-registration-dialog-tab.enum";
import {ValidationResultEnum} from "../../types/validation-result.enum";
import {PasswordResetFormModel} from "./models/password-reset/password-reset-form.model";
import {IUserProfileViewModel} from "./models/profile/user-profile-view-model.interface";
import {NullableNumber} from "../../types/nullable-types";
import {Check} from "../../types/type-checking";
import {UserServiceBroadcastChannelMessages} from "./user-service-broadcast-channel-messages";
import {ClientBroadcastChannel} from "../../utils/client-broadcast-channel";
import {BroadcastChannelsEnum} from "../../utils/broadcast-channels.enum";
import {UserRefreshTokenStorage} from "./user-refresh-token-storage";
import {
    BookingSessionClassesOfService
} from "../dot-rez-api/session/booking-session/booking-session-classes-of-service";
import {BookingSessionCodesSettings} from "../dot-rez-api/session/booking-session/booking-session-codes-settings";
import {IChangeExpiredPasswordRequest} from "../airline-webapi/requests/chanage-expired-password.request";
import {ILoginResponse} from "../airline-webapi/responses/login.response";



const RECREATE_SESSION_INTERVAL = TimeSpan.fromMinutes(13);
const ACCELERATED_RECREATE_SESSION_INTERVAL = TimeSpan.fromSeconds(6);

export class UserService extends ServiceBase implements IUserService {
    constructor(services: IServiceFactory) {
        super(services);

        this._currentUser = this._createInitialUser();

        this._currentUser.getSession().then(() => {
            this._lastSuccessfulRecreateSession = Date.now();
            this._startRecreateSessionTimer(RECREATE_SESSION_INTERVAL);
        }).catch(() => {
            this._startRecreateSessionTimer(ACCELERATED_RECREATE_SESSION_INTERVAL);
        });

        reaction(() => this.services.application.isActive,
            async (isActive) => {
                if(!isActive) {
                    return;
                }

                // if _lastSuccessfulRecreateSession was not set yet it means that there is a pending create session
                if(Check.isNullOrUndefined(this._lastSuccessfulRecreateSession)) {
                    return;
                }

                const now = this.services.time.currentDate.getTime();
                const diff = now - this._lastSuccessfulRecreateSession;
                if (diff >= RECREATE_SESSION_INTERVAL.totalMilliseconds) {
                    await this._tryRecreateSession();
                } else {
                    this._startRecreateSessionTimer(TimeSpan.fromMilliseconds(RECREATE_SESSION_INTERVAL.totalMilliseconds - diff));
                }
            });

        makeObservable(this, {
            _currentUser: observable.ref,
            _codesSettingsPromise: observable.ref,
        });

        this._broadcastChannel = new ClientBroadcastChannel<UserServiceBroadcastChannelMessages>(BroadcastChannelsEnum.UserService, this.services);
        this._broadcastChannel.subscribe(this._broadcastChannelMessageHandler);
    }

    _currentUser: IUserModel;

    private _recreateSessionTimer: any = null;
    private _lastSuccessfulRecreateSession: NullableNumber = null;
    private _broadcastChannel: ClientBroadcastChannel<UserServiceBroadcastChannelMessages>;

    private _createInitialUser(): IUserModel {
        const refreshTokenStorage = UserRefreshTokenStorage.fromStorage(this.services);

        if(refreshTokenStorage?.hasRefreshToken) {
            return new AuthorizedUserModel(this.services, {
                refreshTokenStorage: refreshTokenStorage
            });
        } else {
            return new AnonymousUserModel(this.services);
        }
    }

    private async _setCurrentUser(user: IUserModel): Promise<void> {
        if(this._currentUser === user) {
            return;
        }

        await user.profile.waitForProfileInitialization();

        this._currentUser.dispose();

        runInAction(() => {
            this._currentUser = user;
            this._codesSettingsPromise = null;
        });
    }


    get isAuthorized(): boolean {
        return this._currentUser.isAuthorized;
    }
    
    getSession(): Promise<IDotRezUserSession> {
        return this._currentUser.getSession();
    }

    private async _tryRecreateSession(): Promise<void> {
        clearTimeout(this._recreateSessionTimer);

        try {
            await this._currentUser.reCreateSession();
            this._lastSuccessfulRecreateSession = this.services.time.currentDate.getTime();
            this._startRecreateSessionTimer(RECREATE_SESSION_INTERVAL);
        } catch (err) {
            this.services.logger.error('Failed to recreate user session', err);
            this._startRecreateSessionTimer(ACCELERATED_RECREATE_SESSION_INTERVAL);
        }
    }

    private _startRecreateSessionTimer(timeInterval: TimeSpan): void {
        clearTimeout(this._recreateSessionTimer);
        this._recreateSessionTimer = setTimeout(async () => {
            await this._tryRecreateSession();
        }, timeInterval.totalMilliseconds);
    }



    async loginWithCredentials(credentials: ILoginCredentials): Promise<void> {
        if(this._loginInProgressPromise) {
            return this._loginInProgressPromise;
        }

        this._loginInProgressPromise = this._currentUser.loginWithCredentials(credentials).then(async user => {
            await this._setCurrentUser(user);
            this._broadCastLogin(user);
        }).finally(async () => {
            this._loginInProgressPromise = null;
        });
        return this._loginInProgressPromise;
    }

    private _loginInProgressPromise: Promise<void> | null = null;
    loginWithForm(options?: ILoginWithFormOptions): Promise<void> {

        if(this._loginInProgressPromise) {
            return this._loginInProgressPromise;
        }


        options = {
            defaultTab: LoginAndRegistrationDialogTabEnum.Login,
            ...options
        };

        this._loginInProgressPromise = this._currentUser.loginWithForm(options).then(async user => {
            await this._setCurrentUser(user);
            this._broadCastLogin(user);
        }).finally(() => {
            this._loginInProgressPromise = null;
        });

        return this._loginInProgressPromise;

    }

    async logout(): Promise<void> {
        if(!this.isAuthorized) {
            return;
        }

        const prevUserNameHash = this._currentUser.profile.userNameHash;
        await this._executeLogout();
        this._broadCastLogout(prevUserNameHash);

    }

    private async _executeLogout(): Promise<void> {
        if(!this.isAuthorized) {
            return;
        }

        const user = await this._currentUser.logout();
        await this._setCurrentUser(user);

        await this.services.navigator.goHome({
            skipRoutingGuards: true
        });

    }

    async createTransientBookingSession(): Promise<IDotRezBookingSession> {
        return await this._currentUser.createTransientBookingSession();
    }

    async createBookingSession(options: ICreateBookingSessionOptions): Promise<IDotRezBookingSession> {
        return await this._currentUser.createBookingSession(options);
    }

    async createFlightSearchSession(options: ICreateFlightSearchSessionOptions): Promise<IDotRezBookingSession> {
        return await this._currentUser.createFlightSearchSession(options);
    }


    async sendResetPasswordLink(emailAddress: string): Promise<ValidationResultEnum> {
        const response = await this.services.airlineWebapi.sendResetPasswordLink(emailAddress);
        if(response.hasError) {
            this.services.alert.showError(this.services.language.translateApiError(response.errorCode));
            return ValidationResultEnum.Failure;
        }

        return ValidationResultEnum.Success;
    }

    async confirmRegistration(customerNumber: string): Promise<void> {
        await this.services.loadingIndicator.execute({
            action: async () => {
                await this.services.airlineWebapi.confirmRegistration(customerNumber);
            }
        });


        await this.services.dialogFactory.showRegistrationConfirmation();

        this._currentUser.registrationConfirmed();

        if(this.isAuthorized) {
            await this.logout();
        }

        await this.loginWithForm();
    }

    async resetPassword(token: string): Promise<void> {
        await this.services.dialogFactory.showPasswordReset({
           form: new PasswordResetFormModel(token, this.services)
        });
    }

    async switchUser(user: IUserModel): Promise<void> {
        await this.logout()
        await this._setCurrentUser(user);
        this._broadCastLogin(user);
    }

    private async _executeAuthorized(action: () => Promise<void>): Promise<void> {
        await this.loginWithForm();
        if(this.isAuthorized) {
            await action();
        }
    }

    async showProfile(): Promise<void> {
        await this._executeAuthorized(async () => {
            this.services.navigator.routes.user.profile.activate();
        });
    }

    get profile(): IUserProfileViewModel {
        return this._currentUser.profile;
    }

    async changePassword(): Promise<void> {
        const result = await this._currentUser.changePassword();
        this._broadCastChangePassword(result);
    }

    async changeExpiredPassword(options: IChangeExpiredPasswordOptions): Promise<IChangeExpiredPasswordResult | null> {
        const newPassword = await this.services.dialogFactory.showChangeExpiredPassword();
        if(!newPassword) {
            return null;
        }


        const request: IChangeExpiredPasswordRequest = {
            userName: options.userName,
            oldPassword: options.oldPassword,
            newPassword: newPassword,
            domain: options.domain
        };

        const loginResponse = await this.services.airlineWebapi.changeExpiredPassword(request);
        return {
            loginResponseData: loginResponse,
            newPassword: newPassword
        };
    }

    private _broadcastChannelMessageHandler = async (message: UserServiceBroadcastChannelMessages)=> {
        //This event is received only when login with remember me happens in another browser tab
        switch (message.type) {
            case "login":

                if(this.isAuthorized) {
                    if(this._currentUser.profile.userNameHash !== message.payload.userNameHash) {
                        if(message.payload.rememberMe) {
                            await this._executeLogout();
                        } else {
                            return;
                        }
                    }
                } else if(!message.payload.rememberMe) {
                    return;
                }

                const user = new AuthorizedUserModel(this.services, {
                    refreshTokenStorage: new UserRefreshTokenStorage(message.payload.rememberMe, message.payload.refreshToken, this.services)
                });

                await this._setCurrentUser(user);
                break;
            case "logout":
                if(message.payload.userNameHash === this._currentUser.profile.userNameHash) {
                    await this._executeLogout();
                }
                break;
            case "changePassword":
                if(!this.isAuthorized) {
                    return;
                }

                if(this._currentUser.profile.userNameHash === message.payload.userNameHash) {
                    const user = new AuthorizedUserModel(this.services, {
                        refreshTokenStorage: new UserRefreshTokenStorage(this._currentUser.rememberMe, message.payload.newRefreshToken, this.services)
                    });

                    await this._setCurrentUser(user);
                }

                break;
        }
    }

    private _broadCastLogin(loggedInUser: IUserModel): void {
        if(loggedInUser.isAuthorized && loggedInUser.refreshToken) {
            this._broadcastChannel.sendMessage({
                type: "login",
                payload: {
                    userNameHash: loggedInUser.profile.userNameHash,
                    refreshToken: loggedInUser.refreshToken,
                    rememberMe: loggedInUser.rememberMe,
                }
            })
        }
    }

    private _broadCastLogout(previousUserNameHash: string): void {
        if(previousUserNameHash !== this._currentUser.profile.userNameHash){
            // If previous userNameHash is different from the current userNameHash it means that indeed a logout happened
            this._broadcastChannel.sendMessage({
                type: "logout",
                payload: {
                    userNameHash: previousUserNameHash,
                }
            })
        }
    }

    private _broadCastChangePassword(changePasswordResult: ILoginResponse | null): void {
        if(!this.isAuthorized || !changePasswordResult) {
            return;
        }
        this._broadcastChannel.sendMessage({
            type: "changePassword",
            payload: {
                userNameHash: this._currentUser.profile.userNameHash,
                newRefreshToken: changePasswordResult.refreshToken
            }
        })
    }
    _codesSettingsPromise:  Promise<BookingSessionCodesSettings> | null = null;
    getCodesSettings(): Promise<BookingSessionCodesSettings> {
        if(!this._codesSettingsPromise) {
            this._codesSettingsPromise = this._currentUser.getSession().then(session => {
                return session.getCodesSettings();
            }).catch(err => {
                this.services.logger.error('Failed to get codes settings', err);
                this._codesSettingsPromise = null;
                throw err;
            })
        }

        return this._codesSettingsPromise;
    }

    async getClassesOfService(): Promise<BookingSessionClassesOfService> {
        const codes = await this.getCodesSettings();
        return codes.classesOfService;

    }

}
